import type { ApiErrorBody, AuthSession } from '@mexp/shared';

/** API origin. Empty in development (Vite proxies `/api`); set VITE_API_BASE when the API lives elsewhere. */
export const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined)?.replace(/\/$/, '') ?? '';
const PREFIX = '/api/v1';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export function errorMessage(error: unknown, fallback = 'Something went wrong. Please try again.'): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof TypeError) return 'Cannot reach the server. Check your connection.';
  return error instanceof Error && error.message ? error.message : fallback;
}

// ---- Session plumbing (the access token lives only in memory) -------------------------------------

let accessToken: string | null = null;
let handlers: {
  onSession?: (session: AuthSession) => void;
  onSignedOut?: () => void;
  onPasswordChangeRequired?: () => void;
} = {};

const tokenListeners = new Set<(token: string | null) => void>();

export function setAccessToken(token: string | null): void {
  if (token === accessToken) return;
  accessToken = token;
  tokenListeners.forEach((listener) => listener(token));
}

export function getAccessToken(): string | null {
  return accessToken;
}

/** Calls `listener` whenever the access token changes (sign-in, renewal, sign-out). Returns the unsubscribe function. */
export function onAccessTokenChange(listener: (token: string | null) => void): () => void {
  tokenListeners.add(listener);
  return () => tokenListeners.delete(listener);
}

export function setSessionHandlers(next: typeof handlers): void {
  handlers = next;
}

let refreshing: Promise<AuthSession | null> | null = null;

/**
 * Exchanges the httpOnly refresh cookie for a new access token. Concurrent callers share one request,
 * and tabs take turns through the Web Locks API so a rotated cookie is never replayed.
 */
export function refreshSession(): Promise<AuthSession | null> {
  refreshing ??= (async () => {
    const run = async (): Promise<AuthSession | null> => {
      const res = await fetch(`${API_BASE}${PREFIX}/auth/refresh`, { method: 'POST', credentials: 'include' });
      if (res.status === 204 || !res.ok) return null; // 204 = no session cookie
      const session = (await res.json()) as AuthSession;
      setAccessToken(session.accessToken);
      return session;
    };
    try {
      return typeof navigator !== 'undefined' && navigator.locks
        ? await navigator.locks.request('mexp-refresh', run)
        : await run();
    } catch {
      return null;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

// ---- Requests ------------------------------------------------------------------------------------

export type Query = Record<string, string | number | boolean | null | undefined>;

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Query;
  signal?: AbortSignal;
}

export function buildUrl(path: string, query?: Query): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
  }
  const qs = params.toString();
  return `${API_BASE}${PREFIX}${path}${qs ? `?${qs}` : ''}`;
}

async function toApiError(res: Response): Promise<ApiError> {
  let body: Partial<ApiErrorBody> = {};
  try {
    body = (await res.json()) as ApiErrorBody;
  } catch {
    /* non-JSON error */
  }
  return new ApiError(res.status, body.code ?? 'ERROR', body.message ?? res.statusText ?? 'Request failed', body.details);
}

export async function api<T>(path: string, options: RequestOptions = {}, retried = false): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

  const res = await fetch(buildUrl(path, options.query), {
    method: options.method ?? 'GET',
    headers,
    credentials: 'include',
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
    signal: options.signal,
  });

  if (res.status === 401 && !retried && !path.startsWith('/auth/login')) {
    const session = await refreshSession();
    if (session) {
      handlers.onSession?.(session);
      return api<T>(path, options, true);
    }
    handlers.onSignedOut?.();
  }
  if (!res.ok) {
    const error = await toApiError(res);
    if (error.code === 'PASSWORD_CHANGE_REQUIRED') handlers.onPasswordChangeRequired?.();
    throw error;
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const http = {
  get: <T>(path: string, query?: Query, signal?: AbortSignal) => api<T>(path, { query, signal }),
  post: <T>(path: string, body?: unknown) => api<T>(path, { method: 'POST', body: body ?? {} }),
  put: <T>(path: string, body?: unknown) => api<T>(path, { method: 'PUT', body: body ?? {} }),
  patch: <T>(path: string, body?: unknown) => api<T>(path, { method: 'PATCH', body: body ?? {} }),
  delete: <T>(path: string) => api<T>(path, { method: 'DELETE' }),
};

/** Resolves API-relative file URLs (local storage) against the API origin; absolute URLs pass through. */
export function resolveApiUrl(url: string): string {
  return url.startsWith('/') ? `${API_BASE}${url}` : url;
}
